import { useEffect, useState } from 'react';
import { Eye, Users, Smartphone, Globe } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Area, AreaChart } from 'recharts';
import api from '../../lib/api';

export default function AdminAnalyticsPage() {
    const [data, setData] = useState(null);
    const [recent, setRecent] = useState([]);
    const [days, setDays] = useState(30);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        Promise.all([
            api.get('/admin/analytics/overview', { params: { days } }),
            api.get('/admin/analytics/recent', { params: { limit: 30 } }),
        ]).then(([a, b]) => {
            if (cancelled) return;
            setData(a.data || null);
            setRecent(b.data?.items || []);
        }).finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [days]);

    const totals = data?.totals || {};
    const cards = [
        { label: `Views (last ${days}d)`, value: totals.window || 0, icon: Eye },
        { label: 'Unique sessions', value: totals.unique_sessions || 0, icon: Users },
        { label: 'Today', value: totals.today || 0, icon: Eye },
        { label: 'Mobile share', value: `${totals.mobile_share || 0}%`, icon: Smartphone },
    ];

    return (
        <div data-testid="admin-analytics-page" className="space-y-6">
            <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Analytics</h1>
                    <p className="text-sm text-muted-foreground mt-1">Site visitors and traffic overview — first-party, no third-party trackers.</p>
                </div>
                <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                    <SelectTrigger className="w-[160px] bg-white/5 border-white/10" data-testid="analytics-window-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="7">Last 7 days</SelectItem>
                        <SelectItem value="30">Last 30 days</SelectItem>
                        <SelectItem value="90">Last 90 days</SelectItem>
                    </SelectContent>
                </Select>
            </header>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {cards.map((c) => (
                    <div key={c.label} className="dt-glass rounded-xl p-4 sm:p-5">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{c.label}</span>
                            <c.icon className="h-4 w-4 text-primary" />
                        </div>
                        <div className="mt-2 text-2xl sm:text-3xl font-semibold font-display">{typeof c.value === 'number' ? c.value.toLocaleString() : c.value}</div>
                    </div>
                ))}
            </div>

            <div className="dt-glass rounded-xl p-5">
                <h2 className="font-display text-lg font-semibold">Daily traffic</h2>
                <p className="text-xs text-muted-foreground">Page views and unique sessions over the selected window.</p>
                <div className="mt-4 h-72">
                    {loading ? <div className="h-full flex items-center justify-center text-muted-foreground">Loading…</div> : (
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={data?.daily || []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="v-grad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0" stopColor="#00e5ff" stopOpacity={0.45} />
                                        <stop offset="1" stopColor="#00e5ff" stopOpacity={0.02} />
                                    </linearGradient>
                                    <linearGradient id="s-grad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0" stopColor="#48ffaf" stopOpacity={0.35} />
                                        <stop offset="1" stopColor="#48ffaf" stopOpacity={0.02} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                                <XAxis dataKey="date" stroke="rgba(255,255,255,0.45)" tick={{ fontSize: 11 }} />
                                <YAxis stroke="rgba(255,255,255,0.45)" tick={{ fontSize: 11 }} allowDecimals={false} />
                                <Tooltip contentStyle={{ background: 'rgba(8,12,20,0.95)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#e6f9ff', fontSize: 12 }} />
                                <Area type="monotone" dataKey="views" stroke="#00e5ff" fill="url(#v-grad)" strokeWidth={2} name="Page views" />
                                <Area type="monotone" dataKey="sessions" stroke="#48ffaf" fill="url(#s-grad)" strokeWidth={2} name="Sessions" />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <ListCard title="Top pages" items={data?.top_paths} />
                <ListCard title="Top referrers" items={data?.top_referrers} placeholder="No external referrers yet" />
                <ListCard title="Browsers" items={data?.top_browsers} />
                <ListCard title="Operating systems" items={data?.top_os} />
                <ListCard title="Top countries" items={data?.top_countries} placeholder="Country data is captured when available from the CDN." />
                <RecentCard items={recent} />
            </div>
        </div>
    );
}

function ListCard({ title, items, placeholder = '—' }) {
    const max = (items || []).reduce((m, it) => Math.max(m, it.count || 0), 0) || 1;
    return (
        <div className="dt-glass rounded-xl p-5">
            <h3 className="font-display text-base font-semibold">{title}</h3>
            {(!items || items.length === 0) ? <div className="mt-3 text-xs text-muted-foreground">{placeholder}</div> : (
                <ul className="mt-3 space-y-2">
                    {items.slice(0, 8).map((it) => (
                        <li key={it.name} className="text-sm">
                            <div className="flex items-center justify-between gap-3">
                                <span className="truncate text-foreground/85" title={it.name}>{it.name || '(unknown)'}</span>
                                <span className="font-mono text-xs text-muted-foreground">{it.count}</span>
                            </div>
                            <div className="mt-1 h-1 rounded bg-white/5 overflow-hidden">
                                <div className="h-full bg-primary/70" style={{ width: `${Math.round((it.count / max) * 100)}%` }} />
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function RecentCard({ items }) {
    return (
        <div className="dt-glass rounded-xl p-5">
            <h3 className="font-display text-base font-semibold">Recent visits</h3>
            {(!items || items.length === 0) ? <div className="mt-3 text-xs text-muted-foreground">No visits yet.</div> : (
                <div className="mt-3 max-h-72 overflow-auto pr-1">
                    <ul className="space-y-2 text-sm">
                        {items.slice(0, 30).map((it) => (
                            <li key={it.id} className="flex items-center justify-between gap-3 border-b border-white/5 pb-1.5">
                                <span className="truncate text-foreground/85" title={it.path}>{it.path}</span>
                                <span className="font-mono text-[11px] text-muted-foreground whitespace-nowrap">{new Date(it.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
